import { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { X } from 'lucide-react'
import type { FiltrosCasillero as FiltrosType } from '@/types/afip'
import { calcularPeriodo, listarMesesCerrados, type MesPeriodo } from '@/lib/periodo-reporte'
import { hoyEnArgentina } from '@/core/services/tareasRecurrentesService'

interface FiltrosCasilleroProps {
  filtros: FiltrosType
  onChange: (filtros: FiltrosType) => void
  disabled?: boolean
}

/**
 * Milisegundos de quietud que espera una fecha antes de viajar al padre.
 *
 * Una fecha se escribe tecla por tecla y el navegador da por completa cada versión intermedia del
 * año: al escribir 01/01/2025 pasa por los años 2, 20 y 202 antes de llegar a 2025, y cada una es
 * una consulta distinta. El campo muestra lo que se escribe al instante y avisa al padre cuando la
 * escritura se detiene, así cada consulta corresponde a una fecha pedida.
 */
const ESPERA_FECHA_MS = 400

/**
 * Lo que muestra el selector de período. «Últimos 30 días» consulta sin fechas y el servidor aplica
 * su rango por defecto. Un mes es el mes calendario completo, que entra en los 31 días que acepta
 * ARCA. «Personalizado» indica que las fechas se cargaron a mano.
 */
type PeriodoCasillero = 'ultimos_30' | 'personalizado' | MesPeriodo

/** Los 12 meses cerrados anteriores al mes en curso de Argentina, del más reciente al más viejo. */
function mesesSeleccionables(): { value: MesPeriodo; label: string }[] {
  const hoy = hoyEnArgentina()
  const haceUnAnio = `${Number(hoy.slice(0, 4)) - 1}${hoy.slice(4)}`
  return listarMesesCerrados(haceUnAnio, new Date(`${hoy}T00:00`))
}

function hayFiltrosActivos(f: FiltrosType): boolean {
  return Boolean(f.fechaDesde || f.fechaHasta || f.estado || f.tieneAdjunto)
}

export function FiltrosCasillero({
  filtros,
  onChange,
  disabled = false,
}: FiltrosCasilleroProps) {
  const conFiltros = hayFiltrosActivos(filtros)

  const [fechas, setFechas] = useState({
    desde: filtros.fechaDesde ?? '',
    hasta: filtros.fechaHasta ?? '',
  })

  // El aviso lleva las dos fechas, no solo la que se acaba de tocar. Los dos campos comparten la
  // espera, así que escribir en el segundo cancela el aviso del primero: sin esto, esa fecha
  // quedaría a la vista sin entrar en la consulta.
  const fechasRef = useRef(fechas)

  const [periodo, setPeriodo] = useState<PeriodoCasillero>(
    filtros.fechaDesde || filtros.fechaHasta ? 'personalizado' : 'ultimos_30'
  )
  const meses = mesesSeleccionables()

  // El aviso al padre sale de un temporizador, y para entonces el resto de los filtros puede haber
  // cambiado. Se leen de acá para no mandar los que estaban cuando arrancó la espera.
  const filtrosRef = useRef(filtros)
  useEffect(() => {
    filtrosRef.current = filtros
  }, [filtros])

  const esperaRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => () => {
    if (esperaRef.current) clearTimeout(esperaRef.current)
  }, [])

  const cambiarFecha = (campo: 'desde' | 'hasta', value: string) => {
    const siguiente = { ...fechasRef.current, [campo]: value }
    fechasRef.current = siguiente
    setFechas(siguiente)
    setPeriodo('personalizado')

    if (esperaRef.current) clearTimeout(esperaRef.current)
    esperaRef.current = setTimeout(() => {
      onChange({
        ...filtrosRef.current,
        fechaDesde: siguiente.desde || undefined,
        fechaHasta: siguiente.hasta || undefined,
      })
    }, ESPERA_FECHA_MS)
  }

  // Un período elegido avisa al padre en el acto, sin la espera de la escritura a mano.
  const elegirPeriodo = (valor: PeriodoCasillero) => {
    if (valor === 'personalizado') return
    if (esperaRef.current) clearTimeout(esperaRef.current)
    const rango =
      valor === 'ultimos_30' ? { desde: '', hasta: '' } : calcularPeriodo(valor, new Date()).actual
    fechasRef.current = rango
    setFechas(rango)
    setPeriodo(valor)
    onChange({
      ...filtrosRef.current,
      fechaDesde: rango.desde || undefined,
      fechaHasta: rango.hasta || undefined,
    })
  }

  return (
    <div className="flex items-end gap-3 flex-wrap">
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">Período</Label>
        <Select
          value={periodo}
          onValueChange={(val) => elegirPeriodo(val as PeriodoCasillero)}
          disabled={disabled}
        >
          <SelectTrigger className="w-[190px]" aria-label="Período">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="max-h-80">
            <SelectItem value="ultimos_30">Últimos 30 días</SelectItem>
            {periodo === 'personalizado' && (
              <SelectItem value="personalizado">Personalizado</SelectItem>
            )}
            {meses.map((m) => (
              <SelectItem key={m.value} value={m.value}>
                {m.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">Desde</Label>
        <Input
          type="date"
          value={fechas.desde}
          onChange={(e) => cambiarFecha('desde', e.target.value)}
          disabled={disabled}
          className="w-[150px]"
        />
      </div>
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">Hasta</Label>
        <Input
          type="date"
          value={fechas.hasta}
          onChange={(e) => cambiarFecha('hasta', e.target.value)}
          disabled={disabled}
          className="w-[150px]"
        />
      </div>
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">Estado</Label>
        <Select
          value={filtros.estado ?? 'todos'}
          onValueChange={(val) =>
            onChange({
              ...filtros,
              estado:
                val === 'todos'
                  ? undefined
                  : (val as FiltrosType['estado']),
            })
          }
          disabled={disabled}
        >
          <SelectTrigger className="w-[150px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos</SelectItem>
            <SelectItem value="NO_LEIDA">No leída</SelectItem>
            <SelectItem value="LEIDA">Leída</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {conFiltros && !disabled && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            if (esperaRef.current) clearTimeout(esperaRef.current)
            fechasRef.current = { desde: '', hasta: '' }
            setFechas(fechasRef.current)
            setPeriodo('ultimos_30')
            onChange({})
          }}
          className="text-muted-foreground"
        >
          <X className="h-3.5 w-3.5 mr-1" />
          Limpiar
        </Button>
      )}
    </div>
  )
}
